
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import CashVoucherServices from "../../../services/CashVoucherServices";

// ==========================================
// COMPANY DETAILS
// ==========================================

const LOGO_URL =
  "https://indigoinfra.in/img/product/product-1739471679615.jpeg";

const COMPANY = "INDIGO INFRAPROJECTS PVT. LTD.";

const ADDRESS =
  "3rd Floor, Plot No. - 12/B, Pocket 3, Sector - 17, Dwarka, New Delhi - 110 075";

// ==========================================
// FORMAT HELPERS
// ==========================================

const n = (value) => Number(value || 0);

const fmt = (value) =>
  n(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const clean = (value) =>
  String(value ?? "")
    .replace(/[^\x20-\x7E]/g, " ")
    .trim();

const name = (value) =>
  clean(
    typeof value === "object" && value !== null
      ? value.name || value.location || ""
      : value
  );

const dateLabel = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value || "")
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
    : clean(value);

// ==========================================
// AMOUNT IN WORDS (INDIAN SYSTEM)
// ==========================================

const belowThousand = (value) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen"
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety"
  ];

  const parts = [];
  let x = value;

  if (x >= 100) {
    parts.push(
      `${ones[Math.floor(x / 100)]} Hundred`
    );
    x %= 100;
  }

  if (x >= 20) {
    parts.push(tens[Math.floor(x / 10)]);
    x %= 10;
  }

  if (x > 0) {
    parts.push(ones[x]);
  }

  return parts.join(" ");
};

const integerWords = (value) => {
  if (value === 0) return "Zero";

  const groups = [
    [10000000, "Crore"],
    [100000, "Lakh"],
    [1000, "Thousand"],
    [1, ""]
  ];

  let remaining = value;
  const parts = [];

  for (const [size, label] of groups) {
    const count = Math.floor(remaining / size);

    if (count) {
      parts.push(
        `${belowThousand(count)} ${label}`.trim()
      );
    }

    remaining %= size;
  }

  return parts.join(" ");
};

const amountWords = (value) => {
  const paiseTotal = Math.round(n(value) * 100);
  const rupees = Math.floor(paiseTotal / 100);
  const paise = paiseTotal % 100;

  return (
    `Rupees ${integerWords(rupees)}` +
    (paise
      ? ` and ${integerWords(paise)} Paise`
      : "") +
    " Only"
  );
};

// ==========================================
// LOAD EXISTING INDIGO LOGO
// ==========================================

const loadLogo = async () => {
  try {
    const response = await fetch(LOGO_URL, {
      mode: "cors"
    });

    if (!response.ok) {
      throw new Error("Logo request failed");
    }

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);

      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(
      "Cash voucher logo unavailable:",
      error
    );

    return null;
  }
};

// ==========================================
// READ API RESPONSE
// ==========================================

const extractVoucher = (response) => {
  const payload = response?.data?.status
    ? response.data
    : response;

  return (
    payload?.data?.voucher ||
    payload?.voucher ||
    payload?.data?.data?.voucher
  );
};

// ==========================================
// DOWNLOAD CASH VOUCHER PDF
// ==========================================

export const downloadCashVoucherPDF = async (
  voucherId
) => {
  if (!voucherId) {
    throw new Error("Voucher ID is required.");
  }

  // Fetch latest saved voucher details.
  const response =
    await CashVoucherServices.getVoucher(voucherId);

  const voucher = extractVoucher(response);

  if (
    !voucher ||
    !Array.isArray(voucher.entries)
  ) {
    throw new Error(
      "Voucher details are unavailable."
    );
  }

  if (!voucher.entries.length) {
    throw new Error(
      "Voucher contains no entries."
    );
  }

  // Ensure PDF total matches saved entry amounts.
  const actualTotal = voucher.entries.reduce(
    (sum, entry) => sum + n(entry.amount),
    0
  );

  if (
    Math.abs(
      actualTotal - n(voucher.totalAmount)
    ) > 0.01
  ) {
    throw new Error(
      "Voucher amount differs from its entries. Please contact Accounts before printing."
    );
  }

  // ==========================================
  // PAGE CONFIGURATION
  // ==========================================

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const left = 10;
  const right = W - 10;

  const sideW = 43;
  const splitX = left + sideW;

  const mainX = splitX + 3;
  const mainRight = right - 3;
  const mainW = mainRight - mainX;

  const logo = await loadLogo();

  // ==========================================
  // DEBIT ENTRIES
  // ==========================================

  const debitRows = voucher.entries.map(
    (entry, index) => [
      String(index + 1),

      `${
        name(entry.category) || "Uncategorized"
      }\n${clean(entry.remark) || "-"}`,

      fmt(entry.amount)
    ]
  );

 const employee = voucher.passbook?.employee;

const creditName =
  voucher.passbook?.employee?.name ||
  name(voucher.passbook) ||
  "CashBook";

  const projectName =
    name(voucher.store) || "-";

  const number =
    clean(voucher.voucherNumber) || "-";

  const revision = n(voucher.revision);

  const voucherDate = dateLabel(
    voucher.voucherDate
  );

  // ==========================================
  // PAGE FRAME / COMPANY HEADER
  // ==========================================

  const frame = (page) => {
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(55, 55, 55);
    doc.setLineWidth(0.22);

    // Outer border
    doc.rect(
      left,
      10,
      right - left,
      H - 20
    );

    // Left receipt panel separator
    doc.line(
      splitX,
      10,
      splitX,
      H - 10
    );

    // ========================================
    // LEFT RECEIPT PANEL
    // ========================================

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text(
      "RECEIVED",
      left + sideW / 2,
      18,
      { align: "center" }
    );

    doc.text(
      "with thanks from",
      left + sideW / 2,
      23,
      { align: "center" }
    );

    doc.setFontSize(8.5);

    const companyLines =
      doc.splitTextToSize(
        COMPANY,
        sideW - 5
      );

    doc.text(
      companyLines,
      left + sideW / 2,
      36,
      { align: "center" }
    );

    const amountLabel = amountWords(
      voucher.totalAmount
    );

    doc.setFontSize(8.4);

    const words = doc.splitTextToSize(
      `the sum of ${amountLabel}`,
      sideW - 6
    );

    doc.text(
      words,
      left + sideW / 2,
      65,
      { align: "center" }
    );

    const amountY = Math.max(
      96,
      69 + words.length * 4
    );

    doc.rect(
      left + 2,
      amountY,
      sideW - 4,
      15
    );

    doc.setFontSize(12);

    doc.text(
      `Rs. ${fmt(voucher.totalAmount)}`,
      left + sideW / 2,
      amountY + 9.5,
      {
        align: "center",
        maxWidth: sideW - 7
      }
    );

    // ========================================
    // COMPANY LOGO
    // ========================================

    if (logo) {
      try {
        const props =
          doc.getImageProperties(logo);

        const scale = Math.min(
          30 / props.width,
          15 / props.height
        );

        doc.addImage(
          logo,
          "JPEG",
          mainX,
          13,
          props.width * scale,
          props.height * scale
        );
      } catch (error) {
        console.warn(
          "Unable to draw Indigo logo:",
          error
        );
      }
    }

    // ========================================
    // COMPANY NAME / ADDRESS
    // ========================================

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.2);

    doc.text(
      COMPANY,
      mainRight,
      18,
      {
        align: "right",
        maxWidth: mainW - 32
      }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.4);

    doc.text(
      ADDRESS,
      mainRight,
      24,
      {
        align: "right",
        maxWidth: mainW - 32
      }
    );

    doc.line(
      mainX,
      30,
      mainRight,
      30
    );

    // ========================================
    // BLACK CASH VOUCHER TITLE BAR
    // ========================================

    const barW = 70;

    const barX =
      mainX + (mainW - barW) / 2;

    doc.setFillColor(10, 10, 10);

    doc.rect(
      barX,
      32,
      barW,
      10,
      "F"
    );

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);

    doc.text(
      "CASH VOUCHER",
      barX + barW / 2,
      38.7,
      { align: "center" }
    );

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    // ========================================
    // VOUCHER INFORMATION
    // ========================================

    doc.text(
      `Project Name : ${projectName}`,
      mainX,
      47,
      { maxWidth: 95 }
    );

    doc.text(
      `Date : ${voucherDate}`,
      mainX,
      52
    );

    doc.text(
      `Entries : 1 - ${voucher.entries.length} of ${voucher.entries.length}`,
      mainX,
      57
    );

    doc.text(
      `Voucher No : ${number}`,
      mainRight,
      47,
      { align: "right" }
    );

    doc.text(
      `Revision : R${revision}`,
      mainRight,
      52,
      { align: "right" }
    );

    doc.text(
      `Page : ${page}`,
      mainRight,
      57,
      { align: "right" }
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text(
      "DEBIT :-",
      mainX,
      65
    );

    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    doc.text(
      "Generated From Indigo Store Management System",
      mainRight,
      H - 13,
      { align: "right" }
    );
  };

  // ==========================================
  // DRAW FIRST PAGE
  // ==========================================

  frame(1);

  // ==========================================
  // DEBIT TABLE
  // ==========================================

  autoTable(doc, {
    startY: 68,

    margin: {
      left: mainX,
      right: W - mainRight,
      top: 68,
      bottom: 48
    },

    head: [
      [
        "Sr.",
        "Narration / Category",
        "Amount Rs."
      ]
    ],

    body: debitRows,

    theme: "grid",

    showHead: "everyPage",

    styles: {
      font: "helvetica",
      fontSize: 8,
      textColor: [0, 0, 0],
      lineColor: [70, 70, 70],
      lineWidth: 0.18,
      cellPadding: 2.2,
      overflow: "linebreak"
    },

    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center"
    },

    columnStyles: {
      0: {
        cellWidth: 10,
        halign: "center"
      },

      1: {
        cellWidth: mainW - 38
      },

      2: {
        cellWidth: 28,
        halign: "right"
      }
    },

    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        frame(data.pageNumber);
      }
    }
  });

  // ==========================================
  // TOTAL DEBIT
  // ==========================================

  let y =
    doc.lastAutoTable.finalY + 1;

  const needed = 53;

  if (y + needed > H - 24) {
    doc.addPage();

    frame(
      doc.internal.getNumberOfPages()
    );

    y = 68;
  }

  doc.setDrawColor(70, 70, 70);
  doc.setLineWidth(0.18);

  doc.rect(
    mainX,
    y,
    mainW,
    8
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);

  doc.text(
    "Total Debit",
    mainRight - 29,
    y + 5.2,
    { align: "right" }
  );

  doc.text(
    fmt(voucher.totalAmount),
    mainRight - 1.5,
    y + 5.2,
    { align: "right" }
  );

  y += 13;

  // ==========================================
  // CREDIT SECTION
  // ==========================================

  doc.setFontSize(9);

  doc.text(
    "CREDIT :-",
    mainX,
    y
  );

  y += 3;

  const framedPages =
    doc.internal.getNumberOfPages();

  autoTable(doc, {
    startY: y,

    margin: {
      left: mainX,
      right: W - mainRight,
      bottom: 20
    },

    head: [
      [
        "Sr.",
        "Entry By / Voucher No.",
        "Amount Rs."
      ]
    ],

    body: [
      [
        "1",
        `${creditName}\nVoucher No.: ${number}`,
        fmt(voucher.totalAmount)
      ]
    ],

    foot: [
      [
        "",
        "Total Credit",
        fmt(voucher.totalAmount)
      ]
    ],

    theme: "grid",

    showHead: "firstPage",

    styles: {
      font: "helvetica",
      fontSize: 8,
      textColor: [0, 0, 0],
      lineColor: [70, 70, 70],
      lineWidth: 0.18,
      cellPadding: 2.2
    },

    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center"
    },

    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold"
    },

    columnStyles: {
      0: {
        cellWidth: 10,
        halign: "center"
      },

      1: {
        cellWidth: mainW - 38
      },

      2: {
        cellWidth: 28,
        halign: "right"
      }
    },

    didDrawPage: () => {
      if (
        doc.internal.getNumberOfPages() >
        framedPages
      ) {
        frame(
          doc.internal.getNumberOfPages()
        );
      }
    }
  });

  // ==========================================
  // DOWNLOAD PDF
  // ==========================================

  const fileName =
    `${number.replace(/[^a-zA-Z0-9_-]/g, "_")}` +
    `_R${revision}.pdf`;

  doc.save(fileName);

  return fileName;
};
